import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    Paper,
    Divider,
    Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface EmailSettingsTabProps {
    emailSettings: any;
    setEmailSettings: (settings: any) => void;
}

const EmailSettingsTab: React.FC<EmailSettingsTabProps> = ({ emailSettings, setEmailSettings }) => {

    // Helper to update nested state
    const updateSetting = (section: string, key: string, value: any) => {
        if (section === 'root') {
            setEmailSettings({ ...emailSettings, [key]: value });
        } else {
            setEmailSettings({
                ...emailSettings,
                [section]: {
                    ...emailSettings[section],
                    [key]: value
                }
            });
        }
    };

    const handleFileUpload = (event: any, field: string) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // In a real app, upload to server and get URL. Here we use base64 for preview/demo
                // Ideally this should trigger a prop callback to upload file handling
                // For now, let's assume direct base64 works or user provides URL via text input as alternate
                // But wait, the prompt asked to "change logo".
                // Detailed implementation usually requires an upload service logic.
                // I will store the base64 string for now as the value, assuming the backend can handle it or just as a placeholder.
                updateSetting('root', field, reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <Grid container spacing={3}>
            {/* Settings Column */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Entête (Header)</Typography>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Logo</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {emailSettings.logoUrl && (
                            <Box
                                component="img"
                                src={emailSettings.logoUrl}
                                sx={{ height: 50, maxWidth: 100, objectFit: 'contain', border: '1px solid #eee' }}
                            />
                        )}
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                        >
                            Uploader Logo
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e, 'logoUrl')}
                            />
                        </Button>
                    </Box>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Type de fond</Typography>
                    <RadioGroup
                        row
                        value={emailSettings.headerType || 'color'}
                        onChange={(e) => updateSetting('root', 'headerType', e.target.value)}
                    >
                        <FormControlLabel value="color" control={<Radio />} label="Couleur unie" />
                        <FormControlLabel value="image" control={<Radio />} label="Image" />
                    </RadioGroup>

                    {emailSettings.headerType === 'image' ? (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="URL de l'image"
                                value={emailSettings.headerValue || ''}
                                onChange={(e) => updateSetting('root', 'headerValue', e.target.value)}
                                helperText="Lien direct vers l'image de fond"
                                size="small"
                            />
                            {/* Optional: Add Upload for Header Image too */}
                        </Box>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption">Couleur de fond (Hex)</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="color"
                                    value={emailSettings.headerValue || '#667eea'}
                                    onChange={(e) => updateSetting('root', 'headerValue', e.target.value)}
                                />
                                <TextField
                                    value={emailSettings.headerValue || ''}
                                    onChange={(e) => updateSetting('root', 'headerValue', e.target.value)}
                                    size="small"
                                />
                            </Box>
                        </Box>
                    )}

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>Couleurs</Typography>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="caption">Texte de l'entête</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="color"
                                    value={emailSettings.colors?.headerText || '#ffffff'}
                                    onChange={(e) => updateSetting('colors', 'headerText', e.target.value)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="caption">Titre Principal</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="color"
                                    value={emailSettings.colors?.title || '#333333'}
                                    onChange={(e) => updateSetting('colors', 'title', e.target.value)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="caption">Fond du Bouton</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="color"
                                    value={emailSettings.colors?.button || '#667eea'}
                                    onChange={(e) => updateSetting('colors', 'button', e.target.value)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="caption">Texte du Bouton</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="color"
                                    value={emailSettings.colors?.buttonText || '#ffffff'}
                                    onChange={(e) => updateSetting('colors', 'buttonText', e.target.value)}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>Pied de page (Footer)</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Texte du footer"
                        value={emailSettings.footer?.text || ''}
                        onChange={(e) => updateSetting('footer', 'text', e.target.value)}
                        placeholder="© 2025 Mon Organisme. Tous droits réservés."
                        sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={emailSettings.footer?.showSocialLinks || false}
                                onChange={(e) => updateSetting('footer', 'showSocialLinks', e.target.checked)}
                            />
                        }
                        label="Afficher les icônes réseaux sociaux (Non implémenté)"
                    />
                </Paper>
            </Grid>

            {/* Preview Column */}
            <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>Aperçu en direct</Typography>
                <Paper
                    sx={{
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#f4f4f4',
                        minHeight: 500
                    }}
                >
                    <Box sx={{ maxWidth: '100%', m: 2, bgcolor: 'white', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                        {/* Header Preview */}
                        <Box sx={{
                            p: 4,
                            textAlign: 'center',
                            background: emailSettings.headerType === 'image'
                                ? `url('${emailSettings.headerValue}') center/cover`
                                : (emailSettings.headerValue || '#667eea')
                        }}>
                            {emailSettings.logoUrl ? (
                                <img src={emailSettings.logoUrl} alt="Logo" style={{ maxHeight: 60, marginBottom: 10 }} />
                            ) : (
                                <Typography variant="h5" sx={{ color: emailSettings.colors?.headerText || '#fff', fontWeight: 'bold' }}>
                                    🎓 MON ORGANISME
                                </Typography>
                            )}
                        </Box>

                        {/* Content Preview */}
                        <Box sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ color: emailSettings.colors?.title || '#333', fontWeight: 'bold', mb: 2 }}>
                                Bienvenue sur la plateforme !
                            </Typography>
                            <Typography paragraph sx={{ color: '#555' }}>
                                Bonjour Apprenant,
                            </Typography>
                            <Typography paragraph sx={{ color: '#555' }}>
                                Ceci est un exemple d'email pour visualiser vos modifications en temps réel. Le style de cet email s'adaptera automatiquement à vos configurations.
                            </Typography>

                            <Box sx={{ textAlign: 'center', my: 3 }}>
                                <Button
                                    disableElevation
                                    sx={{
                                        bgcolor: emailSettings.colors?.button || '#667eea',
                                        color: emailSettings.colors?.buttonText || '#fff',
                                        py: 1.5,
                                        px: 4,
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            opacity: 0.9,
                                            bgcolor: emailSettings.colors?.button || '#667eea',
                                        }
                                    }}
                                >
                                    Accéder à mon espace
                                </Button>
                            </Box>
                        </Box>

                        {/* Footer Preview */}
                        <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderTop: '1px solid #eee', textAlign: 'center' }}>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Cet email a été envoyé automatiquement.
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                {emailSettings.footer?.text || '© 2025 LMS. Tous droits réservés.'}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default EmailSettingsTab;
